import React, { useState } from "react";
import {
  TextField,
  Box,
  Button,
  Typography,
  Container,
  Card,
} from "@mui/material";
import { setMpinApi } from "./setMpinApi";
import { useNavigate } from "react-router";
import { routePath } from "../../constants/routePath";
import { getSession } from "../../utils/commonUtils";
import { encrypt } from "../../utils/encryption";
import ElegantLogo from "../../assets/elegantPrimeLogo.png";

function SetMpin() {
  const [mPin, setMPin] = useState(new Array(4).fill(""));
  const [confirmMPin, setConfirmMPin] = useState(new Array(4).fill(""));
  const navigate = useNavigate();

  const handlePinChange = (value, index, pinType) => {
    const newPin = pinType === "mPin" ? [...mPin] : [...confirmMPin];
    if (/^[0-9]?$/.test(value)) {
      newPin[index] = value;
      pinType === "mPin" ? setMPin(newPin) : setConfirmMPin(newPin);
    }
  };

  const handlePinKeyUp = (e, index, pinType) => {
    const nextInput = document.querySelector(
      `input[name=${pinType}-${index + 1}]`
    );
    const prevInput = document.querySelector(
      `input[name=${pinType}-${index - 1}]`
    );

    if (e.key === "Backspace" && prevInput) {
      prevInput.focus();
    } else if (nextInput && e.target.value !== "") {
      nextInput.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (mPin.join("") !== confirmMPin.join("")) {
      alert("M-Pin and Confirm M-Pin do not match.");
    } else {
      getSession("eleGentUser", (data) => {
        let obj = JSON.parse(data);
        const randomId = Math.floor(Math.random() * 10000)
          .toString()
          .padStart(4, "0");
        const pass = mPin.join("") + randomId;

        setMpinApi(
          {
            guid: obj.guId,
            mPin: encrypt(pass, "9z8x7c1m2n3b5l4k"),
            isBiometricAuthentication: false,
          },
          () => {
            navigate(routePath.LOGIN);
          }
        );
      });
    }
  };

  // ✅ Validation logic
  const isPinComplete =
    mPin.every((digit) => digit !== "") &&
    confirmMPin.every((digit) => digit !== "");
  const isPinMatch = mPin.join("") === confirmMPin.join("");
  const isFormValid = isPinComplete && isPinMatch;

  return (
    <Box
      sx={{
        mt: 10,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          width: "100%",
          maxWidth: "1100px",
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: 3,
          background: "linear-gradient(to right, #d3d8f4, #0b0bce)",
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 4,
          }}
        >
          <img
            src={ElegantLogo}
            alt="Elegant Logo"
            style={{ width: "300px", maxWidth: "100%" }}
          />
        </Box>

        {/* Form Section */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 4,
          }}
        >
          <Card
            sx={{
              width: "100%",
              maxWidth: "340px",
              padding: 4,
              borderRadius: 3,
              textAlign: "center",
              boxShadow: "none",
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 600, mb: 2, color: "#01008B", fontSize: 26 }}
            >
              Set MPIN
            </Typography>

            <form onSubmit={handleSubmit}>
              <Typography
                align="left"
                sx={{ fontSize: 16, fontWeight: 500, color: "#01008B" }}
              >
                Enter MPIN
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                {mPin.map((value, index) => (
                  <TextField
                    key={index}
                    name={`mPin-${index}`}
                    value={value}
                    onChange={(e) =>
                      handlePinChange(e.target.value, index, "mPin")
                    }
                    onKeyUp={(e) => handlePinKeyUp(e, index, "mPin")}
                    inputProps={{
                      maxLength: 1,
                      inputMode: "numeric",
                      style: {
                        textAlign: "center",
                        fontSize: "24px",
                        width: "60px",
                        height: "40px",
                      },
                    }}
                    variant="standard"
                    sx={{
                      "& .MuiInput-underline:before": {
                        borderBottomWidth: "3px",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomWidth: "3px",
                      },
                    }}
                  />
                ))}
              </Box>

              <Typography align="left" sx={{fontSize: 16, fontWeight: 500, color: "#01008B"  }}>
                Confirm MPIN
              </Typography>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}
              >
                {confirmMPin.map((value, index) => (
                  <TextField
                    key={index}
                    name={`confirmMpin-${index}`}
                    value={value}
                    onChange={(e) =>
                      handlePinChange(e.target.value, index, "confirmMpin")
                    }
                    onKeyUp={(e) => handlePinKeyUp(e, index, "confirmMpin")}
                    inputProps={{
                      maxLength: 1,
                      inputMode: "numeric",
                      style: {
                        textAlign: "center",
                        fontSize: "24px",
                        width: "60px",
                        height: "40px",
                      },
                    }}
                    variant="standard"
                    sx={{
                      "& .MuiInput-underline:before": {
                        borderBottomWidth: "3px",
                      },
                      "& .MuiInput-underline:after": {
                        borderBottomWidth: "3px",
                      },
                    }}
                  />
                ))}
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={!isFormValid}
                sx={{
                  backgroundColor: "#01008B",
                  color: "#fff",
                  fontWeight: "500",
                  padding: "10px 0",
                  borderRadius: "4px",
                  textTransform: "none",
                  fontSize:18,
                  "&:hover": {
                    backgroundColor: "#01008B",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "#a5a5a5",
                    color: "#eee",
                  },
                }}
              >
                Continue
              </Button>
            </form>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

export default SetMpin;
